import {
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { useMemo } from 'react'
import DateFormat from '../../store/types/enums/DateFormat'
import { ContractLightDTO } from '../../store/types/models/Contract'
import { formatStringDate } from '../../store/utils/dateUtils'
import { StickyCell } from '../../styles/WorklogTableStyles'

type EmploymentExpandedRowProps = {
  contract: ContractLightDTO
  isOpen: boolean
  isManagement?: boolean
}

const EmploymentExpandedRow = ({ contract, isOpen, isManagement }: EmploymentExpandedRowProps) => {
  const totalVacationDays = useMemo(
    () => contract.paidVacationPeriods?.reduce((sum, p) => sum + (p.vacationDays ?? 0), 0) ?? 0,
    [contract.paidVacationPeriods]
  )

  const totalOvertimeHours = useMemo(
    () => contract.paidVacationPeriods?.reduce((sum, p) => sum + (p.overtimeHours ?? 0), 0) ?? 0,
    [contract.paidVacationPeriods]
  )

  if (!contract.paidVacationPeriods?.length) return null

  return (
    <>
      {isManagement && (
        <StickyCell sx={{ pb: 0, pt: 0, borderRight: '2px solid', borderColor: 'background.elevation8' }} />
      )}
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
        <Collapse in={isOpen} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
          <Stack
            sx={{
              borderTop: 0,
              borderColor: 'background.elevation8',
              borderRadius: '0 0 1em 1em',
              width: '100%',
              py: 2,
              gap: 2,
            }}
          >
            <Stack justifyContent="space-between" direction="row">
              <Typography component={'h3'} variant={'h6'} color="text.highEmphasis">
                {t('page.employments.table.vacationPeriods')}
              </Typography>
            </Stack>
            <Table size="small">
              <TableHead sx={{ '& .MuiTableCell': { color: 'text.highEmphasis' } }}>
                <TableRow>
                  <TableCell>{t('page.employments.table.from')}</TableCell>
                  <TableCell>{t('page.employments.table.to')}</TableCell>
                  <TableCell>{t('page.employments.table.numberOfDays')}</TableCell>
                  <TableCell>{t('page.employments.table.overtime')}</TableCell>
                  <TableCell>{t('page.employments.table.lastEdition')}</TableCell>
                  <TableCell>{t('page.employments.table.lastEditor')}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {contract.paidVacationPeriods.map(period => (
                  <TableRow key={period.id}>
                    <TableCell>
                      <Typography variant="body2">{dayjs(period.startDate).format(DateFormat.DayMonthYear)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{dayjs(period.endDate).format(DateFormat.DayMonthYear)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{period.vacationDays}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{period.overtimeHours} h</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatStringDate(dayjs(period.modifiedAt).utc(true).toString(), true)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {period.editedBy?.firstName} {period.editedBy?.lastName}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>

              <TableFooter>
                <TableRow>
                  <TableCell colSpan={2} sx={{ border: '0px', borderTop: 1, pt: 2 }}>
                    <Typography sx={{ fontWeight: 700 }}>{t('page.vacationView.sum')}</Typography>
                  </TableCell>
                  <TableCell align="left" sx={{ border: '0px', borderTop: 1 }}>
                    <Typography sx={{ fontWeight: 700 }}>{totalVacationDays}</Typography>
                  </TableCell>
                  <TableCell align="left" colSpan={3} sx={{ border: '0px', borderTop: 1 }}>
                    <Typography sx={{ fontWeight: 700 }}>{`${totalOvertimeHours} h`}</Typography>
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </Stack>
        </Collapse>
      </TableCell>
      {isManagement && (
        <StickyCell
          align="center"
          sx={{
            borderLeft: '2px solid',
            borderLeftColor: 'background.elevation8',
            right: 0,
            pb: 0,
            pt: 0,
            '&:hover': {
              cursor: 'auto',
            },
          }}
        />
      )}
    </>
  )
}

export default EmploymentExpandedRow
